import { Button } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import HeaderBread from "../../components/HeaderBread";
import Layout from "../../Layout";
import CustomHeaderTitle from "../../components/constants/CustomHeaderTitle";
import SwitchBar from "../../components/Pages/SwitchBar";
import SearchField from "../../components/SearchField";
import CustomizedTable from "../../components/UI/CustomizedTable";
import { useDataLayerValue } from "../../DataLayer/DataLayer";

import { getAncillary } from "../../utils/ApiCalls/Ancillary";
import { getBookingErrorlogs } from "../../utils/ApiCalls/Leads";
import { getVessels } from "../../utils/ApiCalls/SpecialPage/index";
import { addlog } from "../../utils/ApiCalls/Logs";
import { checkPermission } from "../../utils/Validation/checkPermission";
import { message } from "antd";

const ErrorModule = () => {
  const navigate = useNavigate();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Error Module", route: "/website/bookings/error-module" },
  ];

  const [active, setActive] = useState("listing");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [count, setCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [ancillary, setAncillary] = useState([]);
  const [vessels, setVessels] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [vesselId, setVesselId] = useState("");
  const [type, setType] = useState("");
  const [createdAt, setCreatedAt] = useState(
    JSON.stringify([
      moment().subtract(3, "months").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ])
  );

  const fetchAncillary = useCallback(async () => {
    const data = await getAncillary({ page: 1, limit: 10 });
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setAncillary(data.data || []);
    }
  }, [dispatch]);

  const fetchVessels = useCallback(async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    const data = await getVessels();
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setVessels([...data, { id: null, name: "All" }]);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  }, [dispatch]);

  const getData = useCallback(
    async (currentPage = 1, sortBy, sortOrder) => {
      dispatch({ type: "SET_LOADING", loading: true });
      try {
        const args = {
          search,
          page: currentPage,
          limit: pageSize,
          type,
          created_at: createdAt,
          ...(status && { status }),
          ...(vesselId && { vessel_name: vessels.find(v => v.id === vesselId)?.name }),
          ...(sortBy && { sort_by: sortBy }),
          ...(sortOrder && { sort_order: sortOrder }),
        };

        const res = await getBookingErrorlogs(args);

        if (res.error) {
          dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
        } else {
          setRows(res.data || []);
          setCount(res.count || 0);
          setPendingCount(res.pending_count || 0);
          setSuccessCount(res.success_count || 0);
        }
      } catch (err) {
        console.error(err);
      } finally {
        dispatch({ type: "SET_LOADING", loading: false });
      }
    },
    [search, pageSize, type, createdAt, status, vesselId, vessels, dispatch]
  );

  const sortHandler = async ({ columnKey, order }) => {
    const sortOrder = order === "ascend" ? "asc" : order === "descend" ? "desc" : undefined;
    setRows([]);
    await getData(1, columnKey, sortOrder);
  };

  const saveDownloads = async () => {
    if (!checkPermission(permission, "download_per", "Error Module", window?.location?.pathname)) {
      return message.error("Download permission missing");
    }

    dispatch({ type: "SET_LOADING", loading: true });

    try {
      const args = {
        search,
        type,
        created_at: createdAt,
        ...(status && { status }),
        ...(vesselId && { vessel_name: vessels.find(v => v.id === vesselId)?.name }),
      };

      const res = await getBookingErrorlogs({ ...args, page: 1, limit: 100000 });
      if (res.error || !res.data?.length) return message.error("No records to download");

      const headers = Object.keys(res.data[0]);
      const csvContent = [
        headers.join(","),
        ...res.data.map(row => headers.map(h => `"${row[h] ?? ""}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ErrorModule_${moment().format("YYYYMMDD_HHmmss")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Download complete");
      addlog("CSV Download", "Error Module", args, userDetails.id);
    } catch (err) {
      console.error(err);
      message.error("Download failed");
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  useEffect(() => {
    fetchAncillary();
    fetchVessels();
  }, [fetchAncillary, fetchVessels]);

  useEffect(() => {
    if (!ancillary.length) return;
    setColumns([
      { title: "Sl", dataIndex: "id", key: "id", width: 30, render: (val, rec, idx) => (page - 1) * pageSize + idx + 1 },
      { title: "Booking No", dataIndex: "booking_number", key: "booking_number", sorter: true },
      { title: "Cabin Status", dataIndex: "cabin_status", key: "cabin_status", sorter: true },
      { title: "Ancillary Status", dataIndex: "status", key: "status", sorter: true },
      { title: "Error Message", dataIndex: "error_message", key: "error_message", render: text => text || "--" },
      { title: "Log Time", dataIndex: "log_timestamp", key: "log_timestamp", render: time => moment(time).format("YYYY-MM-DD HH:mm:ss"), sorter: true },
    ]);
  }, [ancillary, page, pageSize]);

  useEffect(() => {
    if (active === "listing" && ancillary.length) {
      getData(page);
    }
  }, [active, ancillary, page, pageSize, search, status, vesselId, type, createdAt, getData]);

  return (
    <Layout type="read_per" module_data="Error Module">
      <CustomHeaderTitle title="Error Module" />
      <HeaderBread navigation={navigation} title="Error Module" />

      <section className="content" style={{ marginTop: 20 }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[{ title: "Error Module", value: "listing" }]}
            activeTab={active}
            mode="default"
            setActiveTab={tab => {
              setCreatedAt(JSON.stringify([moment().subtract(3, "months").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]));
              setActive(tab);
            }}
          />

          {active === "listing" && (
            <div className="card">
              <div className="card-header two_item" style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={saveDownloads}
                  style={{ marginRight: 10, background: "#0ab402", fontWeight: 500 }}
                >
                  Download
                </Button>
              </div>
              <div className="card-body">
                <div className="table_holder">
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                    <SearchField value={search} SearchValue={() => setPage(1)} setValue={e => setSearch(e.target.value)} />
                  </div>
                  <CustomizedTable
                    columns={columns}
                    rows={rows}
                    page={page}
                    setPage={setPage}
                    count={count}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setRows={setRows}
                    sortHandler={sortHandler}
                    title="Error Module"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ErrorModule;


//OLD CODE & LOGICS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
//OLD CODE & LOGICS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
//OLD CODE & LOGICS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
//OLD CODE & LOGICS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

// import { Button } from "@mui/material";
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import HeaderBread from "../../../components/HeaderBread";
// import { useDataLayerValue } from "../../../DataLayer/DataLayer";
// import Layout from "../../../Layout";
// import Swal from "sweetalert2";
// import { DatePicker, message } from "antd";
// import moment from "moment";
// import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
// import Download from "../../../components/Downloads";
// import Logs from "../../../components/Logs";
// import SwitchBar from "../../../components/Pages/SwitchBar";
// import SearchField from "../../../components/SearchField";
// import BookingStatus from "../../../components/UI/BookingStatus";
// import CustomizedTable from "../../../components/UI/CustomizedTable";
// import Location from "../../../components/UI/Location";
// import { getAncillary } from "../../../utils/ApiCalls/Ancillary";
// import { createDownloads } from "../../../utils/ApiCalls/Downloads";
// import { getBookingErrorlogs } from "../../../utils/ApiCalls/Leads";
// import { addlog } from "../../../utils/ApiCalls/Logs";
// import { getVessels } from "../../../utils/ApiCalls/SpecialPage/index";
// import { checkPermission } from "../../../utils/Validation/checkPermission";

// const ErrorModule = () => {
//   const navigate = useNavigate();
//   const navigation = [
//     {
//       title: "Home",
//       route: "/",
//     },
//     {
//       title: "Error Module",
//       route: "/website/bookings/error-module",
//     },
//   ];
//   const [active, setActive] = useState("listing");
//   const [rows, setRows] = useState([]);
//   const [count, setCount] = useState(0);
//   const [columns, setColumns] = useState([]);
//   const [pending_count, setPendingCount] = useState(0);
//   const [ancillary, setAncillary] = useState();
//   const [success_count, setSuccessCount] = useState(0);
//   const [sailing_date, setSaling_date] = useState();
//   const [created_at, setCreatedAt] = useState(
//     JSON.stringify([
//       moment().subtract(3, "months").format("YYYY-MM-DD"),
//       moment().format("YYYY-MM-DD"),
//     ])
//   );
//   const [page, setPage] = useState(1);
//   const [pageSize, setPageSize] = useState(10);

//   const [search, setSearch] = useState("");
//   const [{ userDetails, permission }, dispatch] = useDataLayerValue();
//   const [locationData, setLocationData] = useState([]);
//   const [locationId, setLocationId] = useState("");
//   const [status, setStatus] = useState("");
//   const [disableSpecificDate, setDisableSpecificDate] = useState([]);
//   const [vessel, setVessel] = useState([]);
//   const [vesselId, setVesselId] = useState("");
//   const [type, setType] = useState("");

//   const sortHandler = async (options) => {
//     if (Object.keys(options).length > 0) {
//       let sort, order;
//       sort = options.columnKey;
//       order =
//         options.order === "ascend"
//           ? "asc"
//           : options.order === "descend"
//           ? "desc"
//           : undefined;
//       await getData(1, sort, order);
//     }
//   };
//   const dateRender = (current) => {
//     const isDisabled =
//       !disableSpecificDate.filter(
//         (item) => item.format("YYYY-MM-DD") === current.format("YYYY-MM-DD")
//       ).length > 0;
//     return (
//       <div
//         className="ant-picker-cell-inner"
//         style={{
//           color: isDisabled ? "inherit" : "white",
//           background: isDisabled ? "inherit" : "blue",
//           borderRadius: "100px",
//         }}
//       >
//         {current.date()}
//       </div>
//     );
//   };

//   const setPositionalValue = (data) => {};
//   useEffect(() => {
//     getancillaryData();
//   }, []);
//   const getancillaryData = async () => {
//     let data = await getAncillary({ page: 1, limit: 10 });
//     if (data.error)
//       dispatch({
//         type: "SET_ERROR_MESSAGE",
//         errMess: { message: data.error, type: "error" },
//       });
//     else if (data.data) {
//       setAncillary(data.data);
//     }
//   };
//   useEffect(() => {
//   if (ancillary?.length)
//     setColumns([
//       {
//         title: "Sl",
//         dataIndex: "id",
//         key: "id",
//         width: 30,
//         render: (val, record, index) => (page - 1) * pageSize + index + 1,
//       },
//       {
//         title: "Booking No",
//         dataIndex: "booking_number",
//         key: "booking_number",
//       },
//       {
//         title: "Cabin Status",
//         dataIndex: "cabin_status",
//         key: "cabin_status",
//       },
//       {
//         title: "Ancillary Status",
//         dataIndex: "status",
//         key: "status",
//       },
//       {
//         title: "Action Status",
//         dataIndex: "action_status",
//         key: "action_status",
//       },
//       {
//         title: "Error Message",
//         dataIndex: "error_message",
//         key: "error_message",
//         render: (text) => <div>{text || "--"}</div>,
//       },
//       {
//         title: "Log Time",
//         dataIndex: "log_timestamp",
//         key: "log_timestamp",
//         render: (time) => moment(time).format("YYYY-MM-DD HH:mm:ss"),
//       }
//     ]);
// }, [page, pageSize, ancillary]);


//   useEffect(() => {
//     if (active === "listing" && ancillary) {
//       console.log("rendering")
//       getVesselData();
//       getData(page);
//     }
//   }, [page, pageSize, ancillary, search, status, vesselId, type, created_at]);

//   const getVesselData = async () => {
//     dispatch({
//       type: "SET_LOADING",
//       loading: true,
//     });

//     let vessels = await getVessels();
//     if (vessels.error) {
//       dispatch({
//         type: "SET_ERROR_MESSAGE",
//         errMess: { message: vessels.error, type: "error" },
//       });
//     } else {
//       vessels.push({ id: null, name: "All" });
//       setVessel(vessels);
//     }
//     dispatch({
//       type: "SET_LOADING",
//       loading: false,
//     });
//   };

//   const getData = async (value, sort, order) => {
//   dispatch({ type: "SET_LOADING", loading: true });

//   let args = {
//     search,
//     page: value,
//     limit: pageSize,
//     type,
//     created_at,
//     ...(status && { status }),
//     ...(vesselId && { vessel_name: vessel?.find(v => v.id === vesselId)?.name }),
//   };

//   let res = await getBookingErrorlogs(args);

//   if (res.error) {
//     dispatch({
//       type: "SET_ERROR_MESSAGE",
//       errMess: { message: res.error, type: "error" },
//     });
//   } else if (res.data) {
//     setRows(res.data);          // ✅ API rows
//     setCount(res.count);        // ✅ API count
//     setPendingCount(res.pending_count ?? 0);
//     setSuccessCount(res.success_count ?? 0);
//     setPositionalValue(res.data);
//   }

//   dispatch({ type: "SET_LOADING", loading: false });
// };

//   const handleEdit = async (id, cabin_no) => {
//     // navigate("/bookings/error-module/edit/" + id + "?cabin_no=" + cabin_no);
//     navigate("/website/bookings/error-module/edit/" + '11022684?cabin_no=5563');
//   };
//   const saveDownloads = async () => {
//     if (!checkPermission(permission, "download_per", "Error Module",window?.location?.pathname)) {
//       return message.error("Download permission missing");
//     }

//     if (count === 0) {
//       return message.error("There are no bookings to download");
//     }

//     let args = {
//       search,
//       sailing_date,
//       created_at,
//       ...(status && { status }),
//       ...(locationId && {
//         location_id: locationId === -1 ? undefined : locationId,
//       }),
//     };

//     if (vesselId) {
//       const selectedVessel = vessel?.find(({ id }) => id === vesselId);
//       if (selectedVessel) {
//         args.vessel_name = selectedVessel.name;
//       }
//     }
//     if (JSON.parse(args.created_at).filter((item) => item !== "").length < 2) {
//       Swal.fire({
//         icon: "error",
//         title: "Oops...",
//         text: "Need date range",
//       });
//       return;
//     }
//     const request = {
//       module_name: "error_module",
//       created_by: userDetails?.id,
//       search_params: args,
//     };

//     try {
//       const data = await createDownloads(request);
//       if (data.error) {
//         throw new Error("An error occurs when you try to download");
//       }

//       addlog("Bookings Downloaded", "Error Module", args, userDetails.id);
//       message.success("Download request submitted");
//     } catch (error) {
//       message.error(error.message);
//     }
//   };

//   return (
//     <Layout type={"read_per"} module_data={"Error Module"}>
//       <CustomHeaderTitle title={"Error Module"} />
//       <div>
//         <div>
//           <HeaderBread
//             navigation={navigation}
//             title={"Error Module"}
//           ></HeaderBread>
//         </div>
//         <section className="content" style={{ marginTop: "20px" }}>
//           <div className="container-fluid">
//             <SwitchBar
//               tabs={[
//                 { title: "Error Module", value: "listing" },
//                 { value: "downloads", title: "Downloads" },
//                 { value: "logs", title: "History Logs" },
//               ]}
//               activeTab={active}
//               setActiveTab={(item) => {
//                 setCreatedAt(
//                   JSON.stringify([
//                     moment().subtract(3, "months").format("YYYY-MM-DD"),
//                     moment().format("YYYY-MM-DD"),
//                   ])
//                 );
//                 setActive(item);
//               }}
//             />
//             {active === "listing" && (
//               <div className="card">
//                 <div className="card-header two_item">
//                   <div
//                     style={{
//                       display: "flex",
//                       justifyContent: "flex-end",
//                       gap: "10px",
//                     }}
//                   >
//                   </div>
//                   <div style={{ display: "flex", justifyContent: "flex-end" }}>
//                     <Button
//                       variant="contained"
//                       onClick={() => saveDownloads()}
//                       style={{
//                         marginRight: "10px",
//                         background: "#0ab402",
//                         fontWeight: "500",
//                       }}
//                     >
//                       Download
//                     </Button>
//                   </div>
//                 </div>
//                 <div className="card-body">
//                   <div className="table_holder">
//                     <div
//                       style={{
//                         display: "flex",
//                         alignItems: "flex-end",
//                         justifyContent: "flex-end",
//                       }}
//                     >
//                       <div
//                         style={{ display: "flex", justifyContent: "flex-end" }}
//                       >
//                         <SearchField
//                           value={search}
//                           SearchValue={() => {
//                             setPage(1);
//                           }}
//                           setValue={(e) => {
//                             setSearch(e.target.value);
//                           }}
//                         />
//                       </div>
//                     </div>
//                     <CustomizedTable
//                       setRows={setRows}
//                       setPageSize={setPageSize}
//                       setPage={setPage}
//                       pageSize={pageSize}
//                       page={page}
//                       count={count}
//                       columns={columns}
//                       rows={rows}
//                       sortHandler={sortHandler}
//                       title={"Error Module"}
//                     />
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </section>
//         {active === "downloads" && (
//           <Download
//             type={"Error Module"}
//             slug={"/website/bookings/error-module/edit/"}
//             refreshlog={active}
//             module={"error_module"}
//           />
//         )}
//         {active === "logs" && (
//           <Logs
//             type={"Error Module"}
//             slug={"/website/bookings/error-module/edit/:id"}
//             refreshlog={active}
//           />
//         )}
//       </div>
//     </Layout>
//   );
// };

// export default ErrorModule;
